import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ClipboardList, Play, ShieldCheck, Timer } from 'lucide-react';
import SEO from '../components/SEO';
import { PageContainer } from '../components/PageHeader';
import { useAssessmentBattery } from '../features/cognition/hooks/useAssessmentBattery';
import { useTrialRunner } from '../features/cognition/runner/useTrialRunner';
import { createAssessmentDriver } from '../features/cognition/runner/drivers/assessmentDriver';
import { taskById } from '../features/cognition/runner/tasks';
import RunnerShell from '../features/cognition/runner/components/RunnerShell';
import AssessmentResults from '../features/cognition/runner/components/AssessmentResults';
import { Skeleton, SkeletonPanel, SkeletonRegion } from '../components/ui/Skeleton';
import ErrorBlock from '../features/parentPortal/components/ErrorBlock';
import { pillarById, daysUntil } from '../features/cognition/utils/format';
import TaskExamples from '../features/cognition/runner/components/TaskExamples';

// Hooks cannot be called conditionally, so the runner always gets a descriptor.
const EMPTY_TASK = {
  targets: [],
  timing: () => ({ fixationMs: 0, windowMs: 0, gapMs: 0 }),
  toAnswer: () => ({}),
  practiceExpectation: () => null,
  StimulusView: () => null,
};

// The server chooses the instrument; the battery response is only a prediction
// of that choice, and the two can disagree if the page sat open across an
// alternation boundary. Every field is resolved at access time from
// `driver.taskId()`, which the driver assigns synchronously inside `start()` —
// before the runner captures the first trial's timing, and long before the first
// stimulus paints. Resolving through state would reintroduce the
// render-vs-network race: timing is captured during `runner.start()` itself,
// one render too early for any setState to help.
//
// A Proxy with a `get` trap is used instead of a fixed object literal so that
// every descriptor field, including those added after this file was written,
// reaches its consumer automatically. An allowlist silently drops any field not
// named in it, which caused BlockContextView, feedbackAvailable, targetsFor, and
// the ctx argument to practiceExpectation to be invisible to the runner.
export function serverAuthoritativeTask(driver, predictedTaskId) {
  const resolve = () => taskById(driver.taskId()) || taskById(predictedTaskId) || EMPTY_TASK;
  return new Proxy(
    {},
    {
      get: (_, prop) => resolve()[prop],
      has: (_, prop) => prop in resolve(),
    }
  );
}

export default function GScoreAssessmentPage() {
  const navigate = useNavigate();
  const { pillar } = useParams();
  const { data, loading, error, reload } = useAssessmentBattery();
  const [running, setRunning] = useState(false);
  // Display-only: which instrument the server actually started. Never used to
  // build a stimulus or a payload — that goes through serverAuthoritativeTask.
  const [serverTaskId, setServerTaskId] = useState(null);
  const [startError, setStartError] = useState(null);

  // Correction 1: derive task directly from battery response — no taskId state.
  // A taskId state caused a timing race: the runner was built in the render where
  // taskId was null (holding EMPTY_TASK) and setTaskId would only take effect on
  // the next render, after runner.start() had already been called.
  const status = (data?.pillars || []).find((p) => p.pillar === pillar) || null;
  const nextTaskId = status?.nextTaskId || null;

  const driver = useMemo(() => createAssessmentDriver({ pillar }), [pillar]);
  const runnerTask = useMemo(
    () => serverAuthoritativeTask(driver, nextTaskId),
    [driver, nextTaskId]
  );
  const runner = useTrialRunner({ driver, task: runnerTask });

  const days = status ? daysUntil(status.nextAvailableAt) : null;
  const isUnlocked = Boolean(status?.overdue || days === 0);
  const nextTask = taskById(nextTaskId);
  const runningTask = taskById(serverTaskId) || nextTask;
  const pillarLabel = pillarById(pillar)?.label || 'Assessment';

  const begin = async () => {
    setStartError(null);
    setRunning(true);
    await runner.start();

    const serverTaskId_ = driver.taskId();
    if (!serverTaskId_) return; // start() failed; the runner is already in 'error'.
    setServerTaskId(serverTaskId_);

    if (process.env.NODE_ENV !== 'production' && serverTaskId_ !== nextTaskId) {
      console.warn(
        '[GScoreAssessmentPage] server taskId mismatch: expected',
        nextTaskId,
        'got',
        serverTaskId_,
      );
    }

    // The server picked an instrument this build cannot render. Rendering
    // anything at all here would produce plausible-looking data for the wrong
    // task, so fail loudly instead. The run is left unfinalized and therefore
    // never produces a score.
    if (!taskById(serverTaskId_)) {
      await runner.abandon();
      setRunning(false);
      setStartError(
        'The server selected an assessment this version of the app cannot run. Please update the app and try again.'
      );
    }
  };

  // Tell the runner the run is over, then leave without waiting on it — same
  // pattern as GScoreTrainGamePage. abandon() ends the run locally before it
  // touches the network and swallows any failure, so awaiting it only holds a
  // page the user has already left.
  const exit = () => {
    runner.abandon();
    navigate('/g-score');
  };

  if (running && runner.phase === 'complete') {
    return <AssessmentResults result={runner.finalResult} />;
  }
  if (running && runner.phase !== 'idle') {
    return (
      <RunnerShell
        task={runnerTask}
        runner={runner}
        title={runningTask?.name || 'Assessment'}
        onExit={exit}
      />
    );
  }

  return (
    <>
      <SEO title={`${pillarLabel} Assessment`} path={`/g-score/assessment/${pillar}`} noindex />
      <PageContainer>
        <Link
          to="/g-score"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--space-1)',
            color: 'var(--text-secondary)',
            textDecoration: 'none',
            fontSize: 'var(--text-body-size)',
            fontWeight: 600,
            marginBottom: 'var(--space-4)',
          }}
        >
          <ArrowLeft size={14} strokeWidth={1.5} />
          Back to G-Score
        </Link>

        {loading ? (
          <SkeletonRegion label="Loading your assessment">
            <Skeleton width="42%" height={26} style={{ marginBottom: 'var(--space-3)' }} />
            <Skeleton width="68%" height={14} style={{ marginBottom: 'var(--space-6)' }} />
            <div style={{ display: 'flex', gap: 'var(--space-3)', marginBottom: 'var(--space-5)' }}>
              {Array.from({ length: 3 }, (_, i) => (
                <SkeletonPanel key={i} style={{ flex: 1 }}>
                  <Skeleton width="60%" height={11} style={{ marginBottom: 'var(--space-2)' }} />
                  <Skeleton width="40%" height={18} />
                </SkeletonPanel>
              ))}
            </div>
            <Skeleton height={44} />
          </SkeletonRegion>
        ) : error ? (
          <ErrorBlock message={error} onRetry={reload} />
        ) : !status ? (
          <div
            style={{
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-overlay)',
              padding: 'var(--space-6)',
              textAlign: 'center',
              color: 'var(--text-secondary)',
              fontSize: 'var(--text-body-size)',
              lineHeight: 1.6,
            }}
          >
            <p style={{ margin: '0 0 var(--space-2)', fontWeight: 700, color: 'var(--text-primary)', fontSize: 'var(--text-panel-size)' }}>
              Assessment not yet available
            </p>
            <p style={{ margin: 0 }}>
              This assessment is not part of your current plan. Check back later or return to your
              G-Score dashboard.
            </p>
          </div>
        ) : (
          <div
            style={{
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-overlay)',
              padding: 'var(--space-6)',
            }}
          >
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 'var(--radius-panel)',
                background: 'var(--accent-light)',
                color: 'var(--accent-primary)',
                display: 'grid',
                placeItems: 'center',
                marginBottom: 'var(--space-4)',
              }}
            >
              <ClipboardList size={26} strokeWidth={1.5} />
            </div>

            <h1
              style={{
                margin: '0 0 var(--space-2)',
                fontSize: 'var(--text-section-size)',
                fontWeight: 700,
                color: 'var(--text-primary)',
                letterSpacing: 'var(--text-section-tracking)',
              }}
            >
              {pillarLabel} assessment
            </h1>
            <p
              style={{
                margin: '0 0 var(--space-5)',
                fontSize: 'var(--text-body-size)',
                color: 'var(--text-secondary)',
                lineHeight: 1.6,
                maxWidth: 'var(--frame-prose)',
              }}
            >
              One task, two rounds. Unlike your training games, the difficulty never adjusts and
              there is no feedback once scoring starts — that is what makes the result comparable
              to your own past runs.
            </p>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: 'var(--space-3)',
                marginBottom: 'var(--space-5)',
              }}
            >
              <InfoTile icon={<Timer size={18} strokeWidth={1.5} />} label="Estimated" value="About 4 min" />
              <InfoTile
                icon={<ClipboardList size={18} strokeWidth={1.5} />}
                label="This run"
                value={nextTask?.name || 'Not available in this version'}
              />
              <InfoTile
                icon={<ShieldCheck size={18} strokeWidth={1.5} />}
                label="Cadence"
                value={`Every ${status.cadenceDays} days`}
              />
            </div>

            {nextTask && (
              <div
                style={{
                  background: 'var(--glass-bg-light)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--radius-control)',
                  padding: 'var(--space-4)',
                  marginBottom: 'var(--space-5)',
                }}
              >
                <div
                  style={{
                    fontSize: 'var(--text-label-size)',
                    fontWeight: 700,
                    color: 'var(--text-tertiary)',
                    textTransform: 'uppercase',
                    letterSpacing: 'var(--text-label-tracking)',
                    marginBottom: 'var(--space-3)',
                  }}
                >
                  What you will do
                </div>
                <ul
                  style={{
                    margin: 0,
                    paddingLeft: 'var(--space-4)',
                    fontSize: 'var(--text-body-size)',
                    color: 'var(--text-secondary)',
                    lineHeight: 1.7,
                  }}
                >
                  {nextTask.instructions.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                  <li>You will get a few practice rounds with feedback before scoring begins.</li>
                  <li>Find a quiet spot — your first response is the honest one.</li>
                </ul>
                <TaskExamples task={nextTask} />
              </div>
            )}

            <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                type="button"
                disabled={!isUnlocked || !nextTask}
                onClick={begin}
                style={{
                  padding: 'var(--space-3) var(--space-5)',
                  borderRadius: 'var(--radius-control)',
                  border: 'none',
                  background: isUnlocked && nextTask ? 'var(--btn-primary-bg)' : 'var(--bg-tertiary)',
                  color: isUnlocked && nextTask ? 'var(--btn-primary-text)' : 'var(--text-tertiary)',
                  fontSize: 'var(--text-body-size)',
                  fontWeight: 700,
                  fontFamily: 'inherit',
                  cursor: isUnlocked && nextTask ? 'pointer' : 'not-allowed',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                }}
              >
                <Play size={16} strokeWidth={1.5} />
                {isUnlocked ? 'Begin assessment' : `Unlocks in ${days} day${days === 1 ? '' : 's'}`}
              </button>
              <span style={{ fontSize: 'var(--text-numeric-size)', color: 'var(--text-tertiary)' }}>
                Consumer wellness product — not a diagnostic tool.
              </span>
            </div>
            {isUnlocked && !nextTask && (
              <p
                style={{
                  marginTop: 'var(--space-3)',
                  fontSize: 'var(--text-body-size)',
                  color: 'var(--text-secondary)',
                  lineHeight: 1.5,
                }}
              >
                This instrument is not available in the current version of the app. Please check for
                an update.
              </p>
            )}
            {startError && (
              <p
                role="alert"
                style={{
                  marginTop: 'var(--space-3)',
                  fontSize: 'var(--text-body-size)',
                  color: 'var(--danger)',
                  lineHeight: 1.5,
                }}
              >
                {startError}
              </p>
            )}
          </div>
        )}
      </PageContainer>
    </>
  );
}

function InfoTile({ icon, label, value }) {
  return (
    <div
      style={{
        background: 'var(--glass-bg-light)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-control)',
        padding: 'var(--space-3) var(--space-4)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', color: 'var(--text-secondary)' }}>
        {icon}
        <div
          style={{
            fontSize: 'var(--text-label-size)',
            fontWeight: 700,
            color: 'var(--text-tertiary)',
            textTransform: 'uppercase',
            letterSpacing: 'var(--text-label-tracking)',
          }}
        >
          {label}
        </div>
      </div>
      <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--text-panel-size)', fontWeight: 700, color: 'var(--text-primary)' }}>
        {value}
      </div>
    </div>
  );
}
